import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { LoaderService } from '@app/shared/services/loader.service';
import { FinanceService, MonthHolidaysResponse, ScheduleResponse } from '../finance.service';
import { HolidayCalendarModalComponent } from '../holiday-calendar-modal/holiday-calendar-modal.component';
import { ConfirmEmailModalComponent, ConfirmEmailDialogResult } from '../confirm-email-modal/confirm-email-modal.component';
import { ToasterService } from '@app/shared/services/toaster.service';

interface MonthOption {
  value: number;
  label: string;
}

export interface ActionItemRow {
  dateLabel: string;
  actionItem: string;
  highlight: boolean;
}

@Component({
  selector: 'app-recharge-recovery',
  templateUrl: './recharge-recovery.component.html',
  styleUrl: './recharge-recovery.component.scss',
})
export class RechargeRecoveryComponent implements OnInit {
  readonly currentYear = new Date().getFullYear();
  selectedMonth = new Date().getMonth() + 1;
  readonly months: MonthOption[] = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  monthData: MonthHolidaysResponse | null = null;
  schedule: ScheduleResponse | null = null;
  actionItems: ActionItemRow[] = [];
  loading = false;
  sending = false;
  errorMessage: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly financeService: FinanceService,
    private readonly loaderService: LoaderService,
    private readonly toaster: ToasterService,
  ) {
    this.loaderService.hide();
  }

  openSendConfirm(): void {
    if (!this.schedule) {
      return;
    }
    const year = this.currentYear;
    const month = this.selectedMonth;
    this.financeService.getRecipients().subscribe({
      next: ({ defaultTo, defaultCc }) => {
        const ref = this.dialog.open(ConfirmEmailModalComponent, {
          data: { defaultTo, defaultCc, year, month },
          width: '1040px',
          maxWidth: '95vw',
          height: 'auto',
          autoFocus: false,
          panelClass: 'finance-confirm-email-modal-panel',
        });
        ref.afterClosed().subscribe((result: ConfirmEmailDialogResult | undefined) => {
          if (result) {
            this.doSend(result.toRecipients, result.ccRecipients, result.additionalEmails);
          }
        });
      },
      error: (err) => {
        console.error('Failed to load default recipients', err);
        this.toaster.showError('Unable to load default recipients', 'Error');
      },
    });
  }

  private doSend(toRecipients: string[], ccRecipients: string[], additionalEmails: string[]): void {
    this.sending = true;
    this.financeService
      .sendRechargeRecoveryEmail({
        year: this.currentYear,
        month: this.selectedMonth,
        toRecipients,
        ccRecipients,
        additionalEmails,
      })
      .subscribe({
        next: (res) => {
          this.sending = false;
          if (res?.status === 'SUCCESS') {
            this.toaster.showSuccess(res.message || 'Email sent', 'Success');
          } else {
            this.toaster.showError(res?.message || 'Send failed', 'Error');
          }
        },
        error: (err) => {
          this.sending = false;
          console.error('Send failed', err);
          this.toaster.showError('Failed to send email. Please try again.', 'Error');
        },
      });
  }

  ngOnInit(): void {
    this.loadMonth();
  }

  onMonthChange(): void {
    this.loadMonth();
  }

  loadMonth(): void {
    this.loading = true;
    this.errorMessage = null;
    forkJoin({
      month: this.financeService.getHolidaysForMonth(this.currentYear, this.selectedMonth),
      schedule: this.financeService.getSchedule(this.currentYear, this.selectedMonth),
    }).subscribe({
      next: ({ month, schedule }) => {
        this.monthData = month;
        this.schedule = schedule;
        this.actionItems = this.buildActionItems(schedule);
        this.loading = false;
      },
      error: (err) => {
        this.errorMessage = 'Unable to load data for the selected month.';
        this.loading = false;
        console.error('Recharge Recovery load failed', err);
      },
    });
  }

  private buildActionItems(s: ScheduleResponse): ActionItemRow[] {
    const range = (from: string, to: string) =>
      from === to ? this.formatDate(from) : `${this.formatDate(from)} – ${this.formatDate(to)}`;
    const deadline = (d: string) => this.formatDeadlineDate(d);

    return [
      {
        dateLabel: range(s.step1RangeStart, s.step1Deadline),
        actionItem: 'FAs to connect with ID&T Leader/DRs for allocation updates.',
        highlight: false,
      },
      {
        dateLabel: deadline(s.step1Deadline),
        actionItem: 'Amendments to be shared with D&T India Ops Team.',
        highlight: true,
      },
      {
        dateLabel: range(s.step2RangeStart, s.step2RangeEnd),
        actionItem: 'FAs to share the resource tracker with Onshore POCs / Finance leads for confirmation.',
        highlight: false,
      },
      {
        dateLabel: deadline(s.step2Deadline),
        actionItem: 'Freeze on any new joinee assignments.',
        highlight: true,
      },
      {
        dateLabel: range(s.step3RangeStart, s.step3Deadline),
        actionItem: 'FAs to prepare the final cost sheets based on received updates.',
        highlight: false,
      },
      {
        dateLabel: deadline(s.step3Deadline),
        actionItem: 'Seek D&T India leader approval.',
        highlight: true,
      },
      {
        dateLabel: range(s.step4RangeStart, s.step4RangeEnd),
        actionItem: 'D&T Leaders to provide approval confirmation over email.',
        highlight: false,
      },
      {
        dateLabel: deadline(s.step4Deadline),
        actionItem: 'Central Team to ratify cost sheet data & share with India Finance POC for invoice generation.',
        highlight: true,
      },
    ];
  }

  private formatDate(iso: string): string {
    // iso is "YYYY-MM-DD"; format as "DD Mon YYYY"
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month} ${date.getFullYear()}`;
  }

  private formatDeadlineDate(iso: string): string {
    // iso is "YYYY-MM-DD"; format as "d-MMM-yy" e.g. "8-May-26"
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const yy = String(date.getFullYear() % 100).padStart(2, '0');
    return `${date.getDate()}-${month}-${yy}`;
  }

  monthYearShort(s: ScheduleResponse): string {
    const date = new Date(s.year, s.month - 1, 1);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const yy = String(s.year % 100).padStart(2, '0');
    return `${month}'${yy}`;
  }

  openHolidayCalendar(): void {
    this.dialog.open(HolidayCalendarModalComponent, {
      data: { year: this.currentYear },
      width: '820px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'finance-holiday-modal-panel',
      autoFocus: false,
    });
  }

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  formatDayList(dates: number[]): string {
    if (!dates || dates.length === 0) {
      return 'No holidays';
    }
    return dates.map((d) => this.withOrdinal(d)).join(' & ');
  }

  withOrdinal(day: number): string {
    const j = day % 10;
    const k = day % 100;
    if (k >= 11 && k <= 13) {
      return `${day}th`;
    }
    if (j === 1) {
      return `${day}st`;
    }
    if (j === 2) {
      return `${day}nd`;
    }
    if (j === 3) {
      return `${day}rd`;
    }
    return `${day}th`;
  }
}
